import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { STATIC, en } from "../i18n/translations";
import api, { API } from "../lib/api";

const I18nContext = createContext(null);
export const useI18n = () => useContext(I18nContext);

const LANDING_PREFIXES = ["landing.", "nav.", "auth.", "card."];
function landingSubset() {
  const out = {};
  Object.keys(en).forEach((k) => {
    if (LANDING_PREFIXES.some((p) => k.startsWith(p))) out[k] = en[k];
  });
  return out;
}

function detectLang() {
  const stored = localStorage.getItem("ss_lang");
  if (stored) return stored;
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  return nav;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLang());
  const [autoDict, setAutoDict] = useState({});
  const [autoLoading, setAutoLoading] = useState(false);

  const isStatic = lang === "en" || lang === "es";

  // fetch auto-translation for non en/es languages (landing content)
  useEffect(() => {
    if (isStatic) return;
    const cacheKey = `ss_auto_${lang}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try { setAutoDict(JSON.parse(cached)); return; } catch (e) {}
    }
    setAutoLoading(true);
    axios
      .post(`${API}/translate`, { target_lang: lang, texts: landingSubset() })
      .then((r) => {
        setAutoDict(r.data.translations || {});
        localStorage.setItem(cacheKey, JSON.stringify(r.data.translations || {}));
      })
      .catch(() => setAutoDict({}))
      .finally(() => setAutoLoading(false));
  }, [lang, isStatic]);

  const setLang = useCallback((l) => {
    localStorage.setItem("ss_lang", l);
    setLangState(l);
    // persist locale so flashcards are generated in this language (ignored if logged out)
    api.post("/me/locale", { locale: l }).catch(() => {});
  }, []);

  const dict = useMemo(() => {
    if (lang === "es") return STATIC.es;
    if (lang === "en") return STATIC.en;
    return { ...en, ...autoDict };
  }, [lang, autoDict]);

  const t = useCallback((key) => dict[key] ?? en[key] ?? key, [dict]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, isStatic, autoLoading }}>
      {children}
    </I18nContext.Provider>
  );
}
